import React from "react";
import GlobalWrapper from "../../../../components/Wrapper/GlobalWrapper";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  Typography,
  Button,
  Alert,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  reportUpload,
  requestleaveGrant,
} from "../../../../redux/features/leaveSlice";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate } from "react-router-dom";

const GrandRequest = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((state) => state.leave);

  const navigate = useNavigate();

  const uploadFile = async (file, dispatch) => {
    try {
      const formData = new FormData();
      formData.append("incomeScreenshot", file);
      const response = await dispatch(reportUpload(formData));
      return response.payload?.data?.filename || "";
    } catch (error) {
      console.error("File upload failed:", error);
      return "";
    }
  };

  const ValidationSchema = Yup.object().shape({
    fromDate: Yup.date().nullable().required("Pick From Date"),
    toDate: Yup.date().nullable().required("Pick To Date"),
    reasonType: Yup.string().required("Select a reason"),
    reason: Yup.string().when("reasonType", {
      is: "other",
      then: (schema) => schema.required("Please provide your reason"),
    }),
    scrnShot: Yup.string()
      .nullable()
      .when("reasonType", {
        is: "medical",
        then: (schema) => schema.required("Please upload a medical report"),
      }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fromDate: "",
      toDate: "",
      reasonType: "",
      reason: "",
      leaveTypeId: 5, // wfh Leave
      scrnShot: null,
      scrnShotDisplay: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      const payload = {
        fromDate: values.fromDate
          ? dayjs(values.fromDate).format("YYYY-MM-DD")
          : "",
        toDate: values.toDate ? dayjs(values.toDate).format("YYYY-MM-DD") : "",
        leaveTypeId: values.leaveTypeId,
        scrnShot: values.scrnShot,
        reason: values.reasonType === "medical" ? "medical" : values.reason,
      };

      dispatch(requestleaveGrant(payload)).then((res) => {
        if (res.payload.status === "success") {
          enqueueSnackbar(res.payload.message, { variant: "success" });
          navigate("/employeeservices/leave-grant-requests");
          formik.resetForm();
        } else {
          enqueueSnackbar(res.payload.message, { variant: "error" });
        }
      });
    },
  });

  return (
    <GlobalWrapper title={"Grant Request"}>
      <Box sx={{ width: { xs: "100%", sm: "50%" }, padding: 0 }}>
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Stack spacing={2} direction={"column"} width={"100%"}>
              <Stack
                direction={"row"}
                width={"100%"}
                spacing={1}
                justifyContent={"space-between"}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From Date"
                    format="DD-MM-YYYY"
                    value={
                      formik.values.fromDate
                        ? dayjs(formik.values.fromDate)
                        : null
                    }
                    onChange={(value) =>
                      formik.setFieldValue("fromDate", value)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error:
                          formik.touched.fromDate &&
                          Boolean(formik.errors.fromDate),
                        helperText:
                          formik.touched.fromDate && formik.errors.fromDate,
                      },
                    }}
                  />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="To Date"
                    format="DD-MM-YYYY"
                    value={
                      formik.values.toDate ? dayjs(formik.values.toDate) : null
                    }
                    onChange={(value) => formik.setFieldValue("toDate", value)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error:
                          formik.touched.toDate &&
                          Boolean(formik.errors.toDate),
                        helperText:
                          formik.touched.toDate && formik.errors.toDate,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Stack>

              <FormControl
                fullWidth
                error={
                  formik.touched.reasonType && Boolean(formik.errors.reasonType)
                }
              >
                <InputLabel id="reasonType-label">Reason</InputLabel>
                <Select
                  labelId="reasonType-label"
                  id="reasonType"
                  value={formik.values.reasonType}
                  onChange={(e) =>
                    formik.setFieldValue("reasonType", e.target.value)
                  }
                  input={<OutlinedInput label="Reason" />}
                >
                  <MenuItem value="medical">Medical</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              {formik.errors.reasonType && (
                <Typography color={"error"} variant="caption">
                  {formik.errors.reasonType}
                </Typography>
              )}

              {formik.values.reasonType === "medical" && (
                <FormControl fullWidth>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Upload Medical Report
                  </Typography>
                  <Button variant="outlined" component="label">
                    {formik.values.scrnShot ? "Change File" : "Upload File"}
                    <input
                      type="file"
                      hidden
                      accept=".pdf,image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            const uploadedFile = await uploadFile(
                              file,
                              dispatch
                            );

                            formik.setFieldValue("scrnShot", uploadedFile);

                            formik.setFieldValue("scrnShotDisplay", file.name);

                            formik.setFieldTouched("scrnShot", true, false);
                            formik.setErrors({
                              ...formik.errors,
                              scrnShot: undefined,
                            });
                          } catch (err) {
                            console.error("Upload failed:", err);
                          }
                          e.target.value = null;
                        }
                      }}
                    />
                  </Button>
                  {formik.values.scrnShotDisplay && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {formik.values.scrnShotDisplay}
                    </Typography>
                  )}

                  {formik.touched.scrnShot &&
                    formik.errors.scrnShot &&
                    !formik.values.scrnShotDisplay && (
                      <Typography color="red" sx={{ mt: 1 }} variant="caption">
                        {formik.errors.scrnShot}
                      </Typography>
                    )}
                </FormControl>
              )}

              {formik.values.reasonType === "other" && (
                <TextField
                  fullWidth
                  label="Reason"
                  name="reason"
                  rows={3}
                  placeholder="Type your reason here..."
                  multiline
                  value={formik.values.reason}
                  onChange={formik.handleChange}
                  error={formik.touched.reason && Boolean(formik.errors.reason)}
                  helperText={formik.errors.reason}
                />
              )}
              <TextField
                fullWidth
                label="Total Days"
                variant="filled"
                value={
                  formik.values.fromDate && formik.values.toDate
                    ? dayjs(formik.values.toDate).diff(
                        dayjs(formik.values.fromDate),
                        "day"
                      ) + 1
                    : ""
                }
                disabled
              />
            </Stack>
            <Box sx={{my:2}}>
              <Alert severity="info" variant="filled">
                Note: Work From Home (WFH) can be applied only after receiving approval from both the Reporting Head and HR for the selected dates.
              </Alert>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
              <Stack direction={"row"} spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => formik.resetForm()}
                >
                  Reset
                </Button>
                <Button
                  disabled={loading}
                  size="small"
                  variant="contained"
                  color="primary"
                  type="submit"
                >
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              </Stack>
            </Box>
          </Form>
        </FormikProvider>
      </Box>
    </GlobalWrapper>
  );
};

export default GrandRequest;
